import React, { FunctionComponent } from "react"
import { useTranslation } from "react-i18next"
import { useUnit } from "effector-react"

import IconStarFull from "assets/img/icon-star-full.svg?react"
import IconHistory from "assets/img/icon-history.svg?react"
import IconQRcode from "assets/img/icon-qrcode.svg?react"
import IconCollection from "assets/img/icon-collection.svg?react"
import IconRectangles from "assets/img/icon-rectangles.svg?react"
import IconCopy from "assets/img/icon-copy.svg?react"

import { Clamp, Guid, Lmid as TLmid } from "@/types"
import assetSidebars from "stores/assetSidebars"
import globalAssetOverviewAssetSidebars from "stores/globalAssetOverviewAssetSidebars"
import recentlyClickedLmid from "stores/recentlyClickedLmid"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import { IAssetSidebarProps } from "components/AssetSidebar/AssetSidebar"
import { Grid } from "components/Grid"
import { Flex } from "components/Flex"
import { IGlobalAssetOverviewAssetSidebarProps } from "components/GlobalAssetOverviewAssetSidebar/GlobalAssetOverviewAssetSidebar"
import Button from "components/Button/Button"
import useCopyToClipboard from "components/CopyToClipboard/useCopyToClipboard"
import Tooltip from "components/Tooltip/Tooltip"

type LmidProps = {
    lmid?: TLmid
    assetId?: Guid | null
    assetVersion?: number
    assetVersioned?: boolean
    isCurrentVersion?: boolean
    labeled?: boolean
    inCollection?: boolean
    isNodeAsset?: boolean
    isInvalid?: boolean
    highlight?: string
    clamp?: Clamp
    testId?: string
    isSplit?: boolean
    copy?: boolean
} & (SidebarProps | GlobalSidebarProps)

type SidebarProps = {
    assetSidebar?: Partial<IAssetSidebarProps>
    globalAsset?: false
}

type GlobalSidebarProps = {
    assetSidebar?: Partial<IGlobalAssetOverviewAssetSidebarProps>
    globalAsset: true
}

const Lmid: FunctionComponent<React.PropsWithChildren<LmidProps>> = ({
    lmid,
    assetId,
    assetVersion,
    assetVersioned,
    isCurrentVersion = true,
    labeled,
    inCollection,
    isNodeAsset,
    isInvalid,
    highlight,
    clamp = 1,
    testId,
    isSplit,
    copy,
    assetSidebar,
    globalAsset,
}) => {
    const { t } = useTranslation()
    const copyToClipboard = useCopyToClipboard()
    const highlightedAssetId = useUnit(recentlyClickedLmid.$highlightedAssetId)

    // Check if this LMID should be highlighted (only one at a time via store)
    const isHighlighted = assetId && highlightedAssetId === assetId

    function handleClick() {
        if (!assetId) return
        // Highlight this LMID immediately on click
        recentlyClickedLmid.setHighlightedAssetId(assetId)
            ; (globalAsset ? globalAssetOverviewAssetSidebars : assetSidebars).show({
                ...assetSidebar,
                assetId,
            })
    }

    // Show version icon only for non-current versions
    const showVersionIcon = assetVersioned && !isCurrentVersion

    const iconSize = 18

    const iconsCount = [
        lmid && copy,
        isNodeAsset,
        showVersionIcon,
        labeled,
        inCollection,
    ].filter(Boolean).length

    const spanClassName = isHighlighted ? 'lmid-highlighted' : undefined

    return (
        <span data-lmid-asset-id={assetId} className={spanClassName}>
            <FlexibleContent
                text={lmid}
                highlight={highlight}
                onClick={assetId ? handleClick : undefined}
                className={isInvalid ? 'color-red-600' : (copy ? 'color-brand-blue' : undefined)}
                insertAfter={iconsCount > 0 ?
                    <Grid columns={`repeat(${iconsCount}, auto)`} gap=".8rem" className="lmid-icons">
                        {!!lmid && !!copy && <Button
                            theme="link"
                            onClick={() => copyToClipboard({ text: lmid })}
                            icon={<IconCopy />}
                            size="small"
                        />}
                        {isNodeAsset && <IconStarFull width={iconSize} height={iconSize} className="d-block" />}
                        {showVersionIcon && (
                            <Tooltip trigger="hover" content={t('asset.thisIsPerviousVersion')}>
                                <IconHistory width={iconSize} height={iconSize} className="d-block" />
                            </Tooltip>
                        )}
                        {labeled && <IconQRcode width={iconSize} height={iconSize} className="d-block" />}
                        {inCollection && <IconCollection width={iconSize} height={iconSize} className="d-block" />}
                    </Grid>
                    : undefined}
                insertBefore={isSplit ? <Flex alignItems='center'>
                    <IconRectangles className="h-2rem" />
                </Flex> : undefined}
                clamp={clamp}
                testId={testId}
            />
        </span>
    )
}

export default Lmid
